/**
 * F-FE-N209 — button-only cash tendering.
 *
 * The cash pad used to offer four fixed amounts (500 / 1,000 / 2,500 / 5,000)
 * that each REPLACED the field, so any bill above 5,000 — or any handful of
 * mixed notes — had to be typed, which on a touch counter means summoning the
 * OS keyboard. Two button kinds replace them:
 *
 *  - suggestions, derived from the amount due, that SET the total
 *    (the round-ups a customer actually hands over), and
 *  - note buttons that ADD to it, so any combination of notes can be counted
 *    in, to the nearest Rs 10.
 *
 * Display-only: nothing here touches what the customer is charged — the
 * recorded payment is always the amount due.
 */

/** Notes a customer hands over, largest first. Added by the "+" buttons. */
export const CASH_NOTES = [5000, 1000, 500, 100] as const
export const CASH_SMALL_NOTES = [50, 20, 10] as const

const ROUND_STEPS = [50, 100, 500, 1000, 5000]
const EPS = 0.005

/**
 * Round-up amounts for a bill: the next multiple of 50 / 100 / 500 / 1,000 /
 * 5,000 strictly above the amount due, smallest first, at most four. On a
 * large bill the 50 and 100 round-ups are noise, so steps under 1/200th of the
 * bill are skipped. When there are more than four, the three smallest are kept
 * plus the 5,000 round-up (always the largest) — paying with a big note is the
 * most common case. The amount itself is covered by "Exact", not here.
 */
export function cashSuggestions(amountDue: number): number[] {
  if (!(amountDue > 0) || !Number.isFinite(amountDue)) return []
  const ups = new Set<number>()
  for (const step of ROUND_STEPS) {
    if (step < amountDue / 200 && step !== 5000) continue
    const up = Math.ceil(amountDue / step) * step
    if (up > amountDue + EPS) ups.add(up)
  }
  const sorted = [...ups].sort((a, b) => a - b)
  if (sorted.length <= 4) return sorted
  return [...sorted.slice(0, 3), sorted[sorted.length - 1]]
}

/** One tap on the pad: a suggestion/Exact sets the total, a note adds to it. */
export type TenderStep = { kind: 'set'; amount: number } | { kind: 'add'; note: number }

export function tallySteps(steps: TenderStep[]): number {
  let total = 0
  for (const s of steps) total = s.kind === 'set' ? s.amount : total + s.note
  return Math.round(total * 100) / 100
}

/** How many of each note were added since the last set — the ×N badges. */
export function noteCounts(steps: TenderStep[]): Map<number, number> {
  const counts = new Map<number, number>()
  for (const s of steps) {
    if (s.kind === 'set') counts.clear()
    else counts.set(s.note, (counts.get(s.note) ?? 0) + 1)
  }
  return counts
}

/**
 * The running breakdown shown beside the total, e.g. "8,000 + 500" or
 * "5,000 ×2 + 1,000". Starts from the last set amount (if any), then each
 * added note grouped in the order it was first tapped.
 */
export function describeSteps(steps: TenderStep[]): string {
  let lastSet = -1
  steps.forEach((s, i) => {
    if (s.kind === 'set') lastSet = i
  })
  const parts: string[] = []
  if (lastSet >= 0) parts.push(formatPlain((steps[lastSet] as { amount: number }).amount))
  for (const [note, n] of noteCounts(steps)) {
    parts.push(n > 1 ? `${formatPlain(note)} ×${n}` : formatPlain(note))
  }
  return parts.join(' + ')
}

function formatPlain(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
}
